import React, { useContext, useMemo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'underscore';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ValidationGroup, withError, withCustomValidation, ErrorMessages } from '@bingads-webui-react/validation';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { FormErrorGroup, UnmappedFormErrors, entityIs } from '@bingads-webui-campaign-react/form-error';
import { useFormService } from '@bingads-webui-react/form';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import {
  Collapse,
  useCollapseState,
  ValidatedFixedPanel,
  getPanelErrorProps,
  TextTitle,
} from '@bingads-webui-campaign/wizard-jumbo-card';
import {
  KeywordPlannerPanel,
  getTargetedLocationInfo,
  getSupportedLanguages,
  isAllLocationsUnsupported,
} from '@bingads-webui-campaign-react/copilot-keyword-planner';
import { useWizardService } from '@bingads-webui-react/wizard';
import { withControlErrorHandling, withUnvalidatedControlErrors } from '@bingads-webui-campaign-react/error-handling';
import { ERROR_ENTITY } from '@bingads-webui-campaign-react/search-campaign-data';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';

import { parseKeywords } from './util/parser';
import { getkeywordStepStyles } from './style/keyword-step-style';
import { StepHeader } from '../../shared/step-header';
import { KeywordSuggestion } from './keyword-suggestion';
import { KeywordInput } from './keyword-input';
import KeywordImg from './media/keyword.svg';
import {
  suggestedKeywordItemsToKeywordsInput,
  withNewAddedKeywords,
  filterExistingKeywords,
  getKeywordsAdded,
} from './util/keyword-parse-helper';

const keywordsProp = entityIs(ERROR_ENTITY.Keyword);
const ValidatedKeywordInput = withControlErrorHandling(withUnvalidatedControlErrors(withError(withCustomValidation(KeywordInput))));

export const KeywordsStep = ({
  data,
  onDataChange,
  error,
  wizardPerfMarker,
}) => {
  const {
    serverErrors = [],
    systemErrors = [],
  } = error || {};

  const i18n = useContext(I18nContext);
  const legacyI18n = useMemo(() => buildLegacyI18n(i18n, [ValidationStringMap]), [i18n]);
  const classes = useDefaultStyles(getkeywordStepStyles);
  const { state: { context: { saveFailed: showAllValidationErrors } } } = useFormService();
  const keywordSuggestionPerfMarker = useMemo(() =>
    wizardPerfMarker.createChild('KeywordSuggestion', { autoEnd: true }), [wizardPerfMarker]);
  const {
    Permissions,
    DynamicPermissions,
  } = useContext(ServerContext);
  const permissions = useMemo(() => ({ dynamic: { ...DynamicPermissions }, ...Permissions }), [DynamicPermissions, Permissions]);

  // props data
  const {
    Languages,
    locationCriterions,
    keywordsInput,
    validationResult,
    keywordSuggestionsAdded,
  } = data || {};

  // wizard data
  const { state } = useWizardService();
  const {
    keywordSuggestions,
  } = state.context.wizardData;


  // local state
  const [isOpen, setIsOpen] = useState(false);
  const [
    allCampaignSettingsSections,
    campaignSettingsExpandedSections,
    setCampaignSettingsExpandedSections,
  ] = useCollapseState(
    ['keyword-suggestion', 'keyword-input'] /* all sections */,
    ['keyword-suggestion', 'keyword-input'] /* initially expanded sections */
  );

  const targetedLocationInfo = useMemo(() => getTargetedLocationInfo(locationCriterions, permissions), [locationCriterions, permissions]);
  const supportedLanguages = useMemo(() => getSupportedLanguages(Languages, permissions), [Languages, permissions]);
  const hideKeywordSuggestions = useMemo(() => ((permissions && permissions.dynamic && permissions.dynamic.KeywordPlannerLiteRefactorPhase1) ?
    _.isEmpty(targetedLocationInfo) : isAllLocationsUnsupported(targetedLocationInfo)) || _.isEmpty(supportedLanguages), [
    targetedLocationInfo,
    supportedLanguages,
    permissions,
  ]);
  const filteredKeywordSuggestions = useMemo(() => (filterExistingKeywords({
    keywordSuggestions,
    keywordsInput,
    i18n,
  })), [
    keywordSuggestions,
    keywordsInput,
    i18n,
  ]);

  // handle adding keywords
  const onAddKeywords = useCallback((rawKeywordsInput, keywordsAdded, activity) => {
    const { validResult } = parseKeywords({
      keywordsTextarealVal: rawKeywordsInput,
      i18n,
      activity,
    });
    onDataChange({
      data: {
        ...data,
        keywordsInput: rawKeywordsInput,
        validationResult: validResult,
        keywordSuggestionsAdded: [...keywordSuggestionsAdded, ...keywordsAdded],
      },
      isValid: validResult.valid,
    });
  }, [
    onDataChange,
    i18n,
    data,
    keywordSuggestionsAdded,
  ]);

  const onKeywordInputChanged = useCallback((rawKeywordsInput, activity) => {
    onAddKeywords(rawKeywordsInput, [], activity);
  }, [onAddKeywords]);

  const onAddSuggestedItem = useCallback((index, text, activity) => {
    onAddKeywords(withNewAddedKeywords({
      keywordsInput,
      newAddedKeywords: text,
    }), getKeywordsAdded({ keywordSuggestions: filteredKeywordSuggestions, suggestedItems: [{ itemText: text }] }));
    // eslint-disable-next-line no-unused-expressions
    activity && activity.signal(`onAddMainPageSuggestedKeyword - ${JSON.stringify({
      SuggestedKeyword: [text],
    })}`);
  }, [keywordsInput, onAddKeywords, filteredKeywordSuggestions]);

  const onAddAllSuggestedItems = useCallback((suggestedItems, activity) => {
    const keywordsAdded = getKeywordsAdded({ keywordSuggestions: filteredKeywordSuggestions, suggestedItems });
    onAddKeywords(withNewAddedKeywords({
      keywordsInput,
      newAddedKeywords: suggestedKeywordItemsToKeywordsInput(keywordsAdded),
    }), keywordsAdded);
    // eslint-disable-next-line no-unused-expressions
    activity && activity.signal(`onAddAllMainPageSuggestedKeywords - ${JSON.stringify({
      SuggestedKeywords: _.map(suggestedItems, item => item.itemText),
    })}`);
  }, [keywordsInput, onAddKeywords, filteredKeywordSuggestions]);

  const onToggle = (selectedData, selectedTab, activity) => {
    // eslint-disable-next-line no-unused-expressions
    activity && activity.signal(`onAddKeywordsFromSidePanel - ${JSON.stringify({
      SelectedKeywords: selectedData,
      Tab: selectedTab,
    })}`);
    const filteredSelectedData = filterExistingKeywords({
      keywordSuggestions: selectedData,
      keywordsInput,
      i18n,
    });
    if (!_.isEmpty(filteredSelectedData)) {
      onAddKeywords(withNewAddedKeywords({
        keywordsInput,
        newAddedKeywords: suggestedKeywordItemsToKeywordsInput(filteredSelectedData),
      }), selectedData);
    }
    setIsOpen(!isOpen);
  };

  const onMoreSuggestionsButtonClick = (activity) => {
    setIsOpen(true);
    // eslint-disable-next-line no-unused-expressions
    activity && activity.signal('onClickExploreButton');
  };

  // components
  const keywordsSection = useMemo(() => {
    const keywordSuggestionPanel = hideKeywordSuggestions ? null : (
      <ValidatedFixedPanel key="keyword-suggestion" attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}>
        <KeywordSuggestion
          onAddAllSuggestedItems={onAddAllSuggestedItems}
          onMoreSuggestionsButtonClick={onMoreSuggestionsButtonClick}
          onAddSuggestedItem={onAddSuggestedItem}
          showSuggestionsSectionShimmer={_.isNull(keywordSuggestions)} // keywordSuggestions is null when loading
          suggestedItems={_.map(filteredKeywordSuggestions, keyword =>
            ({ itemText: keyword.Keyword, key: keyword.Keyword }))}
          perfMarker={keywordSuggestionPerfMarker}
        />
      </ValidatedFixedPanel>
    );

    const keywordInputPanel = (
      <ValidatedFixedPanel
        key="keyword-input"
        title={<TextTitle titleKey={_TL_('Keywords')} icon={KeywordImg} topicKey="pop_BA_SearchRevamp_Keywords" />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <ValidatedKeywordInput
          keywordsInput={keywordsInput || ''}
          onKeywordInputChanged={onKeywordInputChanged}
          formErrorProps={{ filter: keywordsProp }}
          validationResult={validationResult || { valid: true, error: [] }}
        />
      </ValidatedFixedPanel>
    );

    const keywords = [
      keywordSuggestionPanel,
      keywordInputPanel,
    ];

    return (
      <div className={classNames('keywords-section')}>
        <Collapse allPanelItems={allCampaignSettingsSections} checkItems skipItemsTypeCheck activeKeys={campaignSettingsExpandedSections} setActiveKeys={setCampaignSettingsExpandedSections}>
          { keywords }
        </Collapse>
      </div>
    );
  }, [
    allCampaignSettingsSections,
    campaignSettingsExpandedSections,
    setCampaignSettingsExpandedSections,
    keywordsInput,
    onAddAllSuggestedItems,
    onAddSuggestedItem,
    onKeywordInputChanged,
    validationResult,
    keywordSuggestions,
    keywordSuggestionPerfMarker,
    hideKeywordSuggestions,
    filteredKeywordSuggestions,
  ]);

  return (
    <div className={classNames(classes.keywordsStep, 'keywords-step')}>
      <ActuationGroup actuationProps={{ isActive: false }}>
        <AttentionBeaconGroup>
          <FormErrorGroup formErrorProps={{ errors: serverErrors, showAllValidationErrors }}>
            <ValidationGroup
              validationProps={{
                // schema,
                // onValidate,
                // data,
                i18n: legacyI18n,
              }}
            >
              <StepHeader
                title={_TL_('Keywords')}
                description={_TL_('Add relevant words or phrases to reach potential customers searching for your products and services. Your ads will show in response to search queries based on the keywords you provide. ')}
              />
              <div className="server-errors">
                <UnmappedFormErrors />
              </div>
              <div className="system-errors">
                <ErrorMessages messages={systemErrors} />
              </div>
              <div className={classes.formContainer}>
                { keywordsSection }
                {isOpen &&
                  <KeywordPlannerPanel
                    isOpen={isOpen}
                    onClose={onToggle}
                    i18n={i18n}
                    targetedLocationInfo={targetedLocationInfo}
                    supportedLanguages={supportedLanguages}
                  />
                }
              </div>
            </ValidationGroup>
          </FormErrorGroup>
        </AttentionBeaconGroup>
      </ActuationGroup>
    </div>
  );
};

KeywordsStep.propTypes = {
  data: PropTypes.shape({}).isRequired,
  onDataChange: PropTypes.func.isRequired,
  error: PropTypes.shape({
    serverErrors: PropTypes.arrayOf(PropTypes.shape({})),
    systemErrors: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  wizardPerfMarker: PropTypes.shape({
    createChild: PropTypes.func.isRequired,
  }).isRequired,
};

KeywordsStep.defaultProps = {
  error: null,
};
